import { useMemo, useState, type FormEvent } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { fieldRegistry } from './registry';
import type {
  FieldDefinition,
  FieldErrors,
  FieldValue,
  FormValues
} from './types';
import type { ApiError } from '@/api/types';

interface DynamicFormProps {
  fields: FieldDefinition[];
  initialValues?: FormValues;
  /** Called on submit. Throw or return a rejected promise to surface server errors. */
  onSubmit: (values: FormValues) => Promise<void>;
  submitLabel?: string;
  /** External error from a recent server response — typically thrown via apiClient. */
  error?: ApiError | null;
}

/**
 * Metadata-driven form renderer. Pulls a field renderer from
 * {@link fieldRegistry} for each {@link FieldDefinition} and tracks values +
 * field-level errors. On submit, posts the value map up to the caller; the
 * caller wires it into the backend via {@code dynamicRecordsApi.create}.
 *
 * Server-side validation feedback: pass the {@link ApiError} from a 400
 * response into the {@code error} prop and the form will spread its
 * {@code fieldErrors[]} onto the matching inputs.
 */
export function DynamicForm({
  fields,
  initialValues = {},
  onSubmit,
  submitLabel = 'Save',
  error
}: DynamicFormProps) {
  const [values, setValues] = useState<FormValues>(() => initialValues);
  const [submitting, setSubmitting] = useState(false);

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => a.displayOrder - b.displayOrder),
    [fields]
  );

  const fieldErrors = useMemo<FieldErrors>(() => {
    const map: FieldErrors = {};
    error?.fieldErrors?.forEach((fe) => {
      map[fe.field] = fe.message;
    });
    return map;
  }, [error]);

  const handleChange = (name: string, next: FieldValue) =>
    setValues((current) => ({ ...current, [name]: next }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        {sortedFields.map((field) => {
          const Renderer = fieldRegistry[field.fieldType];
          if (!Renderer) {
            // Unknown server-side field type — surface loudly so the gap shows.
            return (
              <Box
                key={field.name}
                sx={{ p: 1, border: '1px dashed', borderColor: 'warning.main' }}
              >
                Unsupported field type: {field.fieldType} ({field.name})
              </Box>
            );
          }
          return (
            <Renderer
              key={field.name}
              name={field.name}
              label={field.label}
              required={field.required}
              value={values[field.name] ?? null}
              error={fieldErrors[field.name]}
              onChange={(next) => handleChange(field.name, next)}
              definition={field}
            />
          );
        })}
        {error && !error.fieldErrors?.length && (
          <Box sx={{ color: 'error.main', fontSize: 14 }}>{error.message}</Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ minWidth: 120 }}
          >
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
